// src/components/blocks/BaseBlock.jsx
import React, { useContext, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { BaseBlockProps } from '../../../../../../types/shared/propsV3';
import getParentBlock from '../../../../../../utils/getParentBlockV3';
import { DeckContext } from '../../context/DeckContextV3';
import BlockType from '../../../../../../enums/blockType';
import BLOCK_GAP from '../../../../../../constants/blockGap';
import { getOppositeEdge } from '../../utils/edgeUtils';

const BaseBlock: React.FC<BaseBlockProps> = ({
	block,
	onUpdateBlock,
	setIsDragging,
	disabled,
	children,
	onDragStopCustom,
	nodeRef, // Accept nodeRef as a prop
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const { scaleFactor, zoomScale } = state;
	const dragStartPos = useRef({ x: 0, y: 0 });

	useEffect(() => {
		dispatch({
			type: 'UPDATE_BLOCK_REF',
			payload: {
				id: block.id,
				ref: nodeRef,
			},
		});
	}, [nodeRef, block.id, dispatch]);

	const handleStart = (e, data) => {
		console.log('[BaseBlockV3 handleStart] DRAG START:', {
			blockId: block.id,
			blockLabel: block.label,
			startPosition: { x: data.x, y: data.y },
			blockPosition: block.position,
			timestamp: Date.now(),
		});
		dragStartPos.current = { x: data.x, y: data.y };
		setIsDragging(true);
		console.log('[BaseBlockV3 handleStart] setIsDragging(true) called');
	};

	// Removed handleDrag - update only on stop to prevent snap-back

	// Helper function to recursively update all nested children positions
	const updateNestedChildrenRecursively = (
		parentBlock: Block,
		xChange: number,
		yChange: number
	): Block => {
		return {
			...parentBlock,
			...(parentBlock.landings?.length && {
				landings: parentBlock.landings.map((landing) => {
					const updatedLanding = {
						...landing,
						position: {
							x: (landing.position?.x || 0) + xChange,
							y: (landing.position?.y || 0) + yChange,
						},
					};
					return updateNestedChildrenRecursively(
						updatedLanding,
						xChange,
						yChange
					);
				}),
			}),
			...(parentBlock.stairs?.length && {
				stairs: parentBlock.stairs.map((stair) => {
					const updatedStair = {
						...stair,
						position: {
							x: (stair.position?.x || 0) + xChange,
							y: (stair.position?.y || 0) + yChange,
						},
					};
					return updateNestedChildrenRecursively(
						updatedStair,
						xChange,
						yChange
					);
				}),
			}),
			...(parentBlock.bumpouts?.length && {
				bumpouts: parentBlock.bumpouts.map((bumpout) => {
					const updatedBumpout = {
						...bumpout,
						position: {
							x: (bumpout.position?.x || 0) + xChange,
							y: (bumpout.position?.y || 0) + yChange,
						},
					};
					return updateNestedChildrenRecursively(
						updatedBumpout,
						xChange,
						yChange
					);
				}),
			}),
		};
	};

	const handleStop = (e: React.MouseEvent, data: { x: number; y: number }) => {
		console.log('[BaseBlockV3 handleStop] ====== DRAG STOP ======', {
			blockId: block.id,
			blockLabel: block.label,
			oldPosition: block.position,
			finalPosition: { x: data.x, y: data.y },
			hasCustomStop: !!onDragStopCustom,
			timestamp: Date.now(),
		});

		const isTouchEvent =
			e.type === 'touchend' || (e.nativeEvent && 'touches' in e.nativeEvent);
		const dragThreshold = isTouchEvent ? 15 : 5;

		const dragDistance = Math.sqrt(
			Math.pow(data.x - dragStartPos.current.x, 2) +
				Math.pow(data.y - dragStartPos.current.y, 2)
		);
		if (dragDistance < dragThreshold) {
			// It was a click, not a drag - open modal
			dispatch({ type: 'SELECT_BLOCK', payload: block });
			dispatch({ type: 'OPEN_BLOCK_MODAL', payload: block });
			setIsDragging(false);
			return;
		}

		setIsDragging(false);

		const xChange = data.x - (block.position?.x || 0);
		const yChange = data.y - (block.position?.y || 0);

		// Use recursive function to update all nested children (including grandchildren)
		const updatedBlock = {
			...updateNestedChildrenRecursively(block, xChange, yChange),
			position: { x: data.x, y: data.y },
		};

		// Also update top-level blocks recursively
		const updatedBlocks = state.blocks.map((blk) => {
			if (blk.parentBlock.id == block.id) {
				const blockWithUpdatedPosition = {
					...blk,
					position: {
						x: (blk.position?.x || 0) + xChange,
						y: (blk.position?.y || 0) + yChange,
					},
				};
				return updateNestedChildrenRecursively(
					blockWithUpdatedPosition,
					xChange,
					yChange
				);
			}
			return blk;
		});

		dispatch({ type: 'UPDATE_BLOCKS', payload: updatedBlocks });

		if (onDragStopCustom) {
			onDragStopCustom(updatedBlock);
		} else {
			onUpdateBlock(updatedBlock);
		}
	};

	const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);
	const edgeAttachedToParent = block.edges.find(
		(edge) => edge.attachedToParent
	);

	const edgeAttachedToChild = parentBlock?.edges.find(
		(edge) => edge.label == getOppositeEdge(edgeAttachedToParent?.label)
	);

	const attachedSegmentIndex =
		edgeAttachedToChild?.segments?.findIndex(
			(segment) => segment.id == block.parentBlock.segmentId
		) ?? 0;

	const attachedSegment = edgeAttachedToChild?.segments[attachedSegmentIndex];

	let bounds: any = {};

	if (block.type == BlockType.DECKBOX) {
		if (!parentBlock) {
			if (state.canvasBounds) {
				const { width, height, offsetX, offsetY } = state.canvasBounds;
				const blockWidth = block.displayDimensions.width * scaleFactor;
				const blockHeight = block.displayDimensions.height * scaleFactor;

				bounds = {
					left: -offsetX,
					top: -offsetY,
					right: width - offsetX - blockWidth,
					bottom: height - offsetY - blockHeight,
				};
			} else {
				// console.log(
				// 	'[BaseBlockV3] No canvas bounds available, bounds disabled'
				// );
				bounds = false;
			}
		} else {
			switch (edgeAttachedToParent?.label) {
				case 'Top':
					bounds = {
						left: 0,
						top:
							(parentBlock.position?.y || 0) +
							parentBlock.displayDimensions.height * scaleFactor +
							BLOCK_GAP,
						right: 'parent',
						bottom:
							(parentBlock.position?.y || 0) +
							parentBlock.displayDimensions.height * scaleFactor +
							BLOCK_GAP,
					};
					break;
				case 'Bottom':
					bounds = {
						left: 0,
						top:
							(parentBlock.position?.y || 0) -
							block.displayDimensions.height * scaleFactor -
							BLOCK_GAP,
						right: 'parent',
						bottom:
							(parentBlock.position?.y || 0) -
							block.displayDimensions.height * scaleFactor -
							BLOCK_GAP,
					};
					break;
				case 'Left':
					bounds = {
						left:
							(parentBlock.position?.x || 0) +
							parentBlock.displayDimensions.width * scaleFactor +
							BLOCK_GAP,
						top: 0,
						right:
							(parentBlock.position?.x || 0) +
							parentBlock.displayDimensions.width * scaleFactor +
							BLOCK_GAP,
						bottom: 'parent',
					};
					break;
				case 'Right':
					bounds = {
						left:
							(parentBlock.position?.x || 0) -
							block.displayDimensions.width * scaleFactor -
							BLOCK_GAP,
						top: 0,
						right:
							(parentBlock.position?.x || 0) -
							block.displayDimensions.width * scaleFactor -
							BLOCK_GAP,
						bottom: 'parent',
					};
					break;
			}
		}
	} else if (block.type == BlockType.BUMPOUT) {
		switch (edgeAttachedToParent?.label) {
			case 'Top':
				bounds = {
					left: parentBlock.position?.x || 0,
					top: parentBlock.position?.y || 0,
					right:
						(parentBlock.position?.x || 0) +
						parentBlock.displayDimensions.width * scaleFactor -
						block.displayDimensions.width * scaleFactor,

					bottom: parentBlock.position?.y || 0,
				};
				break;
			case 'Bottom':
				bounds = {
					left: parentBlock.position?.x || 0,
					top:
						(parentBlock.position?.y || 0) +
						parentBlock.displayDimensions.height * scaleFactor -
						block.displayDimensions.height * scaleFactor,
					right:
						(parentBlock.position?.x || 0) +
						parentBlock.displayDimensions.width * scaleFactor -
						block.displayDimensions.width * scaleFactor,

					bottom: parentBlock.position?.y || 0,
				};
				break;
			case 'Left':
				bounds = {
					left: parentBlock.position?.x || 0,
					top: parentBlock.position?.y || 0,
					right: parentBlock.position?.x || 0,

					bottom:
						(parentBlock.position?.y || 0) +
						parentBlock.displayDimensions.height * scaleFactor -
						block.displayDimensions.height * scaleFactor,
				};
				break;
			case 'Right':
				bounds = {
					left:
						(parentBlock.position?.x || 0) +
						parentBlock.displayDimensions.width * scaleFactor -
						block.displayDimensions.width * scaleFactor,
					top: parentBlock.position?.y || 0,
					right: parentBlock.position?.x || 0,
					bottom:
						(parentBlock.position?.y || 0) +
						parentBlock.displayDimensions.width * scaleFactor -
						block.displayDimensions.height * scaleFactor,
				};
				break;
		}
	} else if (
		(block.type == BlockType.LANDING || block.type == BlockType.STAIR) &&
		attachedSegment
	) {
		let blockOffset = 0;

		if (attachedSegmentIndex > 0 && edgeAttachedToChild) {
			blockOffset = edgeAttachedToChild.segments
				.slice(0, attachedSegmentIndex)
				.reduce((acc, segment) => {
					return acc + segment.length;
				}, 0);
		}

		switch (edgeAttachedToParent?.label) {
			case 'Top':
				bounds = {
					left: (parentBlock.position?.x || 0) + blockOffset * scaleFactor,
					top:
						(parentBlock.position?.y || 0) +
						parentBlock.displayDimensions.height * scaleFactor +
						BLOCK_GAP,
					right:
						(parentBlock.position?.x || 0) +
						blockOffset * scaleFactor +
						(attachedSegment.length - block.displayDimensions.width) *
							scaleFactor,
					bottom:
						(parentBlock.position?.y || 0) +
						parentBlock.displayDimensions.height * scaleFactor +
						BLOCK_GAP,
				};
				break;
			case 'Bottom':
				bounds = {
					left: (parentBlock.position?.x || 0) + blockOffset * scaleFactor,
					top:
						(parentBlock.position?.y || 0) -
						block.displayDimensions.height * scaleFactor -
						BLOCK_GAP,
					right:
						(parentBlock.position?.x || 0) +
						blockOffset * scaleFactor +
						(attachedSegment.length - block.displayDimensions.width) *
							scaleFactor,
					bottom:
						(parentBlock.position?.y || 0) -
						block.displayDimensions.height * scaleFactor -
						BLOCK_GAP,
				};
				break;
			case 'Left':
				bounds = {
					left:
						(parentBlock.position?.x || 0) +
						parentBlock.displayDimensions.width * scaleFactor +
						BLOCK_GAP,
					top: (parentBlock.position?.y || 0) + blockOffset * scaleFactor,
					right:
						(parentBlock.position?.x || 0) +
						parentBlock.displayDimensions.width * scaleFactor +
						BLOCK_GAP,
					bottom:
						(parentBlock.position?.y || 0) +
						blockOffset * scaleFactor +
						(attachedSegment.length - block.displayDimensions.height) *
							scaleFactor,
				};
				break;
			case 'Right':
				bounds = {
					left:
						(parentBlock.position?.x || 0) -
						block.displayDimensions.width * scaleFactor -
						BLOCK_GAP,
					top: (parentBlock.position?.y || 0) + blockOffset * scaleFactor,
					right:
						(parentBlock.position?.x || 0) -
						block.displayDimensions.width * scaleFactor -
						BLOCK_GAP,
					bottom:
						(parentBlock.position?.y || 0) +
						blockOffset * scaleFactor +
						(attachedSegment.length - block.displayDimensions.height) *
							scaleFactor,
				};
				break;
		}
	}

	const positionKey = `${block.id}-${block.position?.x}-${block.position?.y}`;

	return (
		<Draggable
			key={positionKey}
			defaultPosition={block.position || { x: 0, y: 0 }}
			onStart={handleStart}
			onStop={handleStop}
			bounds={bounds}
			disabled={disabled}
			nodeRef={nodeRef}
			scale={zoomScale || 1}
		>
			{React.cloneElement(children, {
				ref: nodeRef,
				style: { ...children.props.style, position: 'absolute' },
			})}
		</Draggable>
	);
};

export default BaseBlock;
